import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  VStack,
} from '@ritualco/jutsu'
import { Form, Formik, FormikOnSubmit } from 'formik'
import * as Yup from 'yup'
import { FormikInput, FormikSelect } from 'components/Formik'
import { HeraModal } from 'components/HeraModal'
import { useContent } from 'lib/content/react'
import { Locale } from 'types/locale'

export type LocaleOption = {
  label: string
  value: Locale
}

const ProfileModalSchema = Yup.object({
  firstName: Yup.string().required(),
  locale: Yup.string().required(),
})
export interface ProfileModalValues
  extends Yup.Asserts<typeof ProfileModalSchema> {}

export type ProfileModalProps = {
  /** User's first name to load initially */
  firstName?: string
  /** User's currently selected locale to load initially */
  locale?: string
  /** (Read-only) User's email address to load initially */
  email: string
  /** List of locale options to select from */
  localeOptions: LocaleOption[]
  firstNameText?: string
  emailText?: string
  localeText?: string
  primaryButtonText?: string
  secondaryButtonText?: string
  onClose: Function
  /** Formik onSubmit */
  onSubmit: FormikOnSubmit<ProfileModalValues>
} & Omit<ModalProps, 'children'>

type ProfileModalFormProps = Pick<ProfileModalProps, 'email' | 'localeOptions'>
const ProfileModalForm = ({ email, localeOptions }: ProfileModalFormProps) => {
  const { formatMessage } = useContent()
  return (
    <Form id="profile">
      <VStack spacing="4">
        <FormikInput
          label={formatMessage({ id: 'pmportal.settings.firstNameLabel' })}
          name="firstName"
          isRequired
        />
        <FormikInput
          label={formatMessage({ id: 'pmportal.settings.emailAddressLabel' })}
          name="email"
          value={email}
          isDisabled
        />
        <FormikSelect
          label={formatMessage({ id: 'pmportal.settings.languageLabel' })}
          name="locale"
          isRequired
        >
          {localeOptions.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </FormikSelect>
      </VStack>
    </Form>
  )
}

export const ProfileModal = (props: ProfileModalProps) => {
  const {
    firstName = '',
    locale = '',
    onClose,
    onSubmit,
    ...modalProps
  } = props
  const initialValues: ProfileModalValues = {
    firstName,
    locale,
  }
  const { formatMessage } = useContent()

  return (
    <HeraModal size="lg" onClose={onClose} {...modalProps}>
      <Formik<ProfileModalValues>
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          await onSubmit(values, actions)
          actions.setSubmitting(false)
          onClose()
        }}
        validationSchema={ProfileModalSchema}
      >
        {formikProps => (
          <>
            <ModalHeader>
              {formatMessage({ id: 'pmportal.settings.profile.editTitle' })}
            </ModalHeader>
            <ModalBody>
              <ProfileModalForm {...props} />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="transparent" mr={2} onClick={onClose}>
                {formatMessage({ id: 'pmportal.global.actionCancel' })}
              </Button>
              <Button
                type="submit"
                form="profile"
                isLoading={formikProps.isSubmitting}
              >
                {formatMessage({ id: 'pmportal.global.actionSave' })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </HeraModal>
  )
}
